import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Joyride from 'react-joyride'
import 'react-loading-skeleton/dist/skeleton.css'
import { useNavigate } from 'react-router-dom'

import Skeleton from 'react-loading-skeleton'
import { Heading } from '../../../components/ui/Heading'
import trackEvents from '../../../constants/track-events'
import {
  useNotificationContext,
  usePartnerContext,
  useUserContext
} from '../../../context'
import { delayedAction } from '../../../helpers'
import { trackEvent } from '../../../helpers/analytics'
import {
  LIFE_CYCLE_STAGES,
  useLifecycleStageTracking
} from '../../../hooks/useLifecycleStageTracking'
import { useUpdateSpaceMutation } from '../../../hooks/useUpdateSpaceMutation'
import { useDiscover } from '../hooks/useDiscover'
import { useOnboardingTour } from '../hooks/useOnboardingTour'
import { usePdfGeneration } from '../hooks/usePdfGeneration'
import { useSelectionParams } from '../hooks/useSelectionParams'
import {
  getProfileOptions,
  getVariantFromScoreKey,
  MESSAGE_ROLES,
  MESSAGE_TYPES,
  sendSmartMessageSequence,
  startMatchingProfileIntroAssistantFlow
} from '../utils'
import AssistantButton from './AssistantButton'
import { AssistantSpotlight } from './AssistantSpotlight'
import BetterMatchSuggestions from './BetterMatchSuggestions'
import EnableFlexSwitch from './EnableFlexSwitch'
import IncompleteDatasetWarning from './IncompleteDatasetWarning'
import PrintButton from './PrintButton'
import ProfileDropdown from './ProfileDropdown'
import DimensionMatchBreakdown from './evaluation/DimensionMatchBreakdown'
import MatchScoreCard from './evaluation/MatchScoreCard'
import DashboardUnlockedJoyride from './joyride/DashboardUnlockedJoyride'
import CompletionSummaryModal from './modal/CompletionSummaryModal'
import CompletionModal from './onboarding/CompletionModal'
import HowToContinueModal from './onboarding/HowToContinueModal'
import CultureInsights from './talentMatch/CultureInsights'
import HiringInitiatives from './talentMatch/HiringInitiatives'
import InterviewRecommendation from './talentMatch/InterviewRecommendation'
import PersonalityInsights from './talentMatch/PersonalityInsights'
import ProfessionalInterestsInsights from './talentMatch/ProfessionalInterestsInsights'
import RelatedCareersSection from './talentMatch/RelatedCareersSection'
import SkillsInsights from './talentMatch/SkillsInsights'

export default function TalentMatchSection({
  className,
  career,
  talent,
  matchingProfile,
  isLoading,
  isDatasetComplete
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { generateMatchingReport } = usePdfGeneration()
  const { activeSpace } = useUserContext()

  const hasSentMatchingIntroRef = useRef(false)

  const [uxStep, setUxStep] = useState(0)
  const [showHowToContinueModal, setShowHowToContinueModal] = useState(false)

  const { partner } = usePartnerContext()
  const hasCompletedOnboarding = activeSpace?.hasCompletedOnboarding

  const { success } = useNotificationContext()
  const {
    isFlexibleMatchingEnabled,
    setIsFlexibleMatchingEnabled,
    enableAssistant,
    setShowAssistant,
    sendMessageToAssistant,
    setAssistantIsTyping,
    setAssistantDetail,
    selectedResult,
    setSelectedResult,
    setShouldClickOnMandatoryDimension,
    shouldClickOnMandatoryDimension,
    hideAssistant,
    shouldShowCompletionOnboardingModal,
    setShouldShowCompletionOnboardingModal,
    setRunMatchingProfileJoyridePartTwo,
    setEnableClickOnFirstMiddleFitDimension,
    setEnableClickOnFirstDimension,
    setCompletionSummaryModalOpen,
    runDashboardUnlockedJoyride,
    setRunDashboardUnlockedJoyride,
    assistantSpotlightSelector,
    setShowFeedbackModal,
    showFeedbackModal,
    clearAssistantMessages,
    setAssistantSpotlightSelector,
    setAssistantFocus
  } = useDiscover()
  const updateSpaceMutation = useUpdateSpaceMutation()
  const { removeCareer, removeTalent } = useSelectionParams()

  const { trackLifecycleStage } = useLifecycleStageTracking()

  // Onboarding-Feedback-Umfrage entfernt. Sie war das einzige Gate, das das
  // Dashboard freischaltet (customer_dashboard_meta ONBOARDING/COMPLETED →
  // hasCompletedOnboarding). Damit niemand dauerhaft gesperrt bleibt, schalten
  // wir an der Stelle, an der die Umfrage aufgegangen wäre (showFeedbackModal),
  // direkt frei und springen in den Post-Onboarding-Flow (Joyride).
  useEffect(() => {
    if (!showFeedbackModal) return
    setShowFeedbackModal(false)
    if (hasCompletedOnboarding) return
    updateSpaceMutation.mutate({
      id: activeSpace.id,
      customer_dashboard_meta: JSON.stringify([
        ...activeSpace.customer_dashboard_meta,
        { id: 'ONBOARDING', event: 'ONBOARDING', status: 'COMPLETED' }
      ])
    })
    // Der alte Weg über handleJoyrideCallback setzte BEIDES: das Space-Meta und
    // die Lifecycle-Stage. Beim Entfernen der Feedback-Umfrage (5a50b79e) kam
    // hier nur das Meta mit — die Stage fehlte. Folge: seit Ende Juni läuft
    // ONBOARDING_COMPLETED nicht mehr nach HubSpot (aivy_onboarding_completed_at,
    // lead_progress=100) und Partner.onboarding_completed_at bleibt leer, obwohl
    // die Leute ihr Onboarding abschließen. In Zahlen: August 17 Abschlüsse laut
    // Space-Meta, 0 im Partner-Feld.
    trackOnboardingCompleted()
    clearAssistantMessages()
    hideAssistant()
    setRunDashboardUnlockedJoyride(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFeedbackModal])

  const matchingScore =
    matchingProfile?.overall_insights?.matching_score_to_career || 0

  const talentName = talent?.person
    ? `${talent.person.firstname || talent.person.invitation_firstname} ${talent.person.lastname || talent.person.invitation_lastname}`
    : t('common.unknown_talent')

  const { steps, styles, isTourRunning, spotlightPadding, startTour } =
    useOnboardingTour({
      matchingScore,
      overall_key: matchingProfile?.overall_insights?.overall_key || 'low',
      talentName
    })

  const {
    overall_insights = {},
    interviews_label = '',
    measures_label = '',
    risks_label = '',
    interpretation_label = '',
    skills_dimensions = [],
    chances_label = '',
    insights_title = '',
    our_review_title = '',
    score_visible = false,
    personality_dimensions = [],
    cultural_fit = null,
    riasec = null,
    culture_visible = false,
    interests_visible = false,
    system = { categories: { data: [] } },
    language = 'de'
  } = matchingProfile || {}

  const insights_labels = {
    insights_title,
    chances_label,
    interpretation_label,
    risks_label,
    interviews_label,
    measures_label
  }

  const personalityCategory = system.categories?.data?.[1] || {}
  const skillsCategory = system.categories?.data?.[2] || {}
  const interestsCategory = system.categories?.data?.[3] || {}
  const cultureCategory = system.categories?.data?.[4] || {}

  const {
    matching_score_to_career = 0,
    overall = '',
    overall_key = '',
    insights = {}
  } = overall_insights || {}

  const {
    interviews = {},
    measures = {},
    skills = {},
    culture = {},
    personality = {},
    interests = {}
  } = insights || {}

  const isSkillsAvailable = !!skills_dimensions?.length
  const isPersonalityAvailable = !!personality_dimensions?.length
  const isCultureAvailable = !!cultural_fit && culture_visible
  const isRiaSecAvailable = !!riasec && interests_visible

  const userCultureScores =
    isCultureAvailable && cultural_fit?.user_cultural_fit
      ? {
          HEROS_JOURNEY_score_coopcomp:
            cultural_fit.user_cultural_fit.HEROS_JOURNEY_score_coopcomp
              ?.user_dimension_value,
          HEROS_JOURNEY_score_orderchaos:
            cultural_fit.user_cultural_fit.HEROS_JOURNEY_score_orderchaos
              ?.user_dimension_value,
          group_match: insights?.culture?.group_match
        }
      : null

  useEffect(() => {
    if (uxStep === 1) {
      setRunMatchingProfileJoyridePartTwo(true)
    }
  }, [uxStep])

  useEffect(() => {
    if (matchingProfile === null || matchingProfile === undefined) return

    if (hasCompletedOnboarding) return

    // Prevent duplicate intro message (e.g. React 18 StrictMode double-invokes effects in dev)
    if (hasSentMatchingIntroRef.current) return
    hasSentMatchingIntroRef.current = true

    sendMessageToAssistant({
      id: `requirement_matching_intro_${Date.now()}`,
      role: MESSAGE_ROLES.ASSISTANT,
      type: MESSAGE_TYPES.VIDEO,
      videoMessageProps: {
        isPulsing: true,
        title: t('discover.assistant_onboarding.8.title'),
        description: t('discover.assistant_onboarding.8.description'),
        videoSrc:
          language === 'de'
            ? 'https://aivy-assets.s3.eu-central-1.amazonaws.com/videos/discover-4-de.mp4'
            : 'https://aivy-assets.s3.eu-central-1.amazonaws.com/videos/discover-4-en.mp4',
        ctaPrimary: {
          title: t('discover.assistant_onboarding.8.cta'),
          onClick: () => {
            trackEvent('discover_matching_profile_intro_completed')
            window.scrollTo({ top: 0, behavior: 'smooth' })

            setTimeout(() => {
              setAssistantFocus(false)
              clearAssistantMessages()
              startMatchingProfileIntroAssistantFlow({
                sendMessageToAssistant,
                setAssistantIsTyping,
                setEnableClickOnFirstDimension,
                setAssistantSpotlightSelector,
                t
              })
            }, 1000)
          }
        }
      }
    })
  }, [matchingProfile])

  const trackOnboardingCompleted = () => {
    const storageKey = 'onboardingCompletedTracked'
    if (localStorage.getItem(storageKey) === 'true') return

    trackLifecycleStage({
      lifecycle_stage: LIFE_CYCLE_STAGES.ONBOARDING_COMPLETED,
      lifecycle_stage_before: LIFE_CYCLE_STAGES.ONBOARDING_TALENT_CREATED,
      aivy_form_id: partner.aivy_form_id,
      lead_progress: (5 / 5).toFixed(2),
      partner_id: partner.id
    })
    localStorage.setItem(storageKey, 'true')
  }

  const handleJoyrideCallback = (data) => {
    const { status } = data

    if (status === 'finished' || status === 'skipped') {
      trackEvent(trackEvents.DISCOVER_END_JOYRIDE_ONBOARDING_TOUR)
      trackOnboardingCompleted()
      updateSpaceMutation.mutate({
        id: activeSpace.id,
        customer_dashboard_meta: JSON.stringify([
          ...activeSpace.customer_dashboard_meta,
          {
            id: 'ONBOARDING',
            event: 'ONBOARDING',
            status: 'COMPLETED'
          }
        ])
      })
      // TODO: OPEN DIMENSION
      setShouldClickOnMandatoryDimension(true)
      localStorage.setItem('onboarding_completed', 'true')
    }
  }

  const onClickDownloadReport = () => generateMatchingReport(career, talent)

  const onClickAssistantChanceAndRisk = () => {
    trackEvent(trackEvents.DISCOVER_CLICK_ASSISTANT_CHANCE_AND_RISK)

    const dimensionMap = [
      { available: isSkillsAvailable, label: skillsCategory.name[language] },
      { available: isCultureAvailable, label: cultureCategory.name[language] },
      {
        available: isPersonalityAvailable,
        label: personalityCategory.name[language]
      },
      { available: isRiaSecAvailable, label: interestsCategory.name[language] }
    ]

    const availableDimensions = dimensionMap
      .filter(({ available }) => available)
      .map(({ label }) => label)

    const getContextualTitle = () => {
      const variant = getVariantFromScoreKey(overall_key)

      if (variant === 'success') {
        return t(
          'discover.assistant.chances_and_risks.first_message.title.high_fit',
          { talentName }
        )
      } else if (variant === 'warning') {
        return t(
          'discover.assistant.chances_and_risks.first_message.title.medium_fit',
          { talentName }
        )
      } else {
        return t(
          'discover.assistant.chances_and_risks.first_message.title.low_fit',
          { talentName }
        )
      }
    }

    const getContextualDescription = () => {
      const dimensionCount = availableDimensions.length

      return t(
        'discover.assistant.chances_and_risks.first_message.description',
        { count: dimensionCount, talentName }
      )
    }

    const dimensionHandlers = {
      [skillsCategory.name[language]]: () => ({
        type: MESSAGE_TYPES.SKILLS_DETAILS,
        skillsDetailsProps: {
          skillsCategory,
          overall_insights,
          score_visible,
          our_review_title,
          skills_dimensions,
          language,
          insights_labels
        }
      }),
      [cultureCategory.name[language]]: () => ({
        type: MESSAGE_TYPES.CULTURE_FIT_DETAILS,
        cultureDetailsProps: {
          cultureCategory,
          overall_insights,
          score_visible,
          our_review_title,
          cultural_fit,
          userCultureScores,
          language
        }
      }),
      [personalityCategory.name[language]]: () => ({
        type: MESSAGE_TYPES.PERSONALITY_DETAILS,
        personalityDetailsProps: {
          personalityCategory,
          overall_insights,
          score_visible,
          our_review_title,
          personality_dimensions,
          insights_labels,
          language
        }
      }),
      [interestsCategory.name[language]]: () => ({
        type: MESSAGE_TYPES.INTERESTS_DETAILS,
        interestsDetailsProps: {
          interestsCategory,
          overall_insights,
          score_visible,
          our_review_title,
          riasec,
          insights_labels,
          language
        }
      })
    }

    const handleDimensionSelect = (dimension) => {
      const payload = dimensionHandlers[dimension]?.()
      if (!payload) return
      triggerAssistantMessage({
        message: { id: 1, role: MESSAGE_ROLES.ASSISTANT, ...payload },
        enable: false
      })

      setTimeout(() => {
        sendMessageToAssistant({
          id: 9292,
          role: MESSAGE_ROLES.ASSISTANT,
          type: MESSAGE_TYPES.CATEGORY_SELECTION,
          categorySelectionProps: {
            availableDimensions,
            onDimensionSelect: handleDimensionSelect,
            categories: {
              skills: skillsCategory.name[language],
              culture: cultureCategory.name[language],
              personality: personalityCategory.name[language],
              interests: interestsCategory.name[language]
            }
          }
        })
      }, 5000)
    }

    enableAssistant()
    setAssistantDetail(true)

    sendSmartMessageSequence({
      setIsTyping: setAssistantIsTyping,
      messages: [
        {
          fn: () =>
            sendMessageToAssistant({
              id: 1,
              role: MESSAGE_ROLES.ASSISTANT,
              type: MESSAGE_TYPES.TEXT,
              title: getContextualTitle(),
              description: getContextualDescription()
            }),
          textLength:
            getContextualTitle().length + getContextualDescription().length
        },
        {
          fn: () =>
            sendMessageToAssistant({
              id: 2,
              role: MESSAGE_ROLES.ASSISTANT,
              type: MESSAGE_TYPES.CATEGORY_SELECTION,
              categorySelectionProps: {
                availableDimensions,
                onDimensionSelect: handleDimensionSelect,
                categories: {
                  skills: skillsCategory.name[language],
                  culture: cultureCategory.name[language],
                  personality: personalityCategory.name[language],
                  interests: interestsCategory.name[language]
                }
              }
            }),
          textLength: 100
        }
      ]
    })
  }

  const onClickAssistantRecommendations = () => {
    trackEvent(trackEvents.DISCOVER_CLICK_ASSISTANT_RECOMMENDATIONS)
    triggerAssistantMessage({
      detail: true,
      message: {
        id: 1,
        role: MESSAGE_ROLES.ASSISTANT,
        type: MESSAGE_TYPES.MATCHING_INSIGHTS,
        matchingInsightsProps: { insights_labels, overall_insights }
      }
    })
  }

  const triggerAssistantMessage = ({
    message,
    delay = 3000,
    detail = true,
    enable = true
  }) => {
    if (enable) enableAssistant()
    if (detail) setAssistantDetail(true)
    setAssistantIsTyping(true)

    const timer = setTimeout(() => {
      console.log('triggerAssistantMessage -> sending message', message)
      sendMessageToAssistant(message)
      setAssistantIsTyping(false)
    }, delay)

    return () => clearTimeout(timer)
  }

  const onClickSkillsInsights = () => {
    trackEvent(trackEvents.DISCOVER_CLICK_SKILLS_INSIGHTS)
    triggerAssistantMessage({
      message: {
        id: 1,
        role: MESSAGE_ROLES.ASSISTANT,
        type: MESSAGE_TYPES.SKILLS_DETAILS,
        skillsDetailsProps: {
          skillsCategory,
          overall_insights,
          score_visible,
          our_review_title,
          skills_dimensions,
          language,
          insights_labels
        }
      }
    })
  }

  const onClickCultureInsights = () => {
    trackEvent(trackEvents.DISCOVER_CLICK_CULTURE_INSIGHTS)
    triggerAssistantMessage({
      message: {
        id: 1,
        role: MESSAGE_ROLES.ASSISTANT,
        type: MESSAGE_TYPES.CULTURE_FIT_DETAILS,
        cultureDetailsProps: {
          cultureCategory,
          overall_insights,
          score_visible,
          our_review_title,
          cultural_fit,
          userCultureScores,
          language
        }
      }
    })
  }

  const onClickPersonalityInsights = () => {
    trackEvent(trackEvents.DISCOVER_CLICK_PERSONALITY_INSIGHTS)
    triggerAssistantMessage({
      message: {
        id: 'personality-insights',
        role: MESSAGE_ROLES.ASSISTANT,
        type: MESSAGE_TYPES.PERSONALITY_DETAILS,
        personalityDetailsProps: {
          personalityCategory,
          overall_insights,
          score_visible,
          our_review_title,
          personality_dimensions,
          insights_labels,
          language
        }
      }
    })
  }

  const onClickProfessionalInterestsInsights = () => {
    trackEvent(trackEvents.DISCOVER_CLICK_PROFESSIONAL_INTERESTS_INSIGHTS)
    triggerAssistantMessage({
      message: {
        id: 1,
        role: MESSAGE_ROLES.ASSISTANT,
        type: MESSAGE_TYPES.INTERESTS_DETAILS,
        interestsDetailsProps: {
          interestsCategory,
          overall_insights,
          score_visible,
          our_review_title,
          riasec,
          insights_labels,
          language
        }
      }
    })
  }

  return (
    <div
      id='talent-match-overall'
      className={clsx('w-full rounded-lg bg-white py-6', className)}
    >
      {isLoading && (
        <div className='mb-4 flex justify-center'>
          <span className='text-sm text-darkBlack'>
            {t('discover.talent_match_section.isLoading')}
          </span>
        </div>
      )}
      <Joyride
        steps={steps}
        run={isTourRunning}
        styles={styles}
        hideCloseButton
        spotlightPadding={spotlightPadding}
        continuous
        disableScrolling={false}
        scrollToFirstStep={true}
        callback={handleJoyrideCallback}
      />
      <div className='border-b border-lightGrey px-6 pb-6'>
        <div className='flex flex-row items-start justify-between'>
          <div>
            <Heading level={6} className={'text-primaryBlue'}>
              {isLoading ? (
                <Skeleton />
              ) : (
                t('discover.talent_match_section.title')
              )}
            </Heading>
            <ProfileDropdown
              className='discover-profile-dropdown mt-1'
              selectedProfile={selectedResult}
              profileOptions={getProfileOptions(t)}
              onProfileChange={setSelectedResult}
              isLoading={isLoading}
            />
          </div>
          <EnableFlexSwitch
            enabled={isFlexibleMatchingEnabled}
            setEnabled={setIsFlexibleMatchingEnabled}
            isLoading={isLoading}
          />
        </div>

        {/* {career && talent && (
          <InfoCardPair career={career} talent={talent} isLoading={isLoading} />
        )} */}
        {isDatasetComplete && (
          <>
            <MatchScoreCard
              className={'mt-6'}
              variant={getVariantFromScoreKey(overall_key)}
              score={Math.round(matching_score_to_career)}
              description={overall}
              isLoading={isLoading}
            />
            <BetterMatchSuggestions
              className={'mt-6'}
              careerId={career?.id}
              currentMatchingScore={matching_score_to_career}
            />
          </>
        )}
      </div>

      {!isDatasetComplete ? (
        <IncompleteDatasetWarning career={career} talent={talent} />
      ) : (
        <div className='px-6'>
          <div className='mt-8'>
            <DimensionMatchBreakdown
              matchingProfile={matchingProfile}
              careerId={career?.id}
              talentName={talentName}
              isLoading={isLoading}
              setUxStep={setUxStep}
            />
          </div>
          <div
            id='matching-profile-chances-risks'
            className='mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2'
          >
            <div className='space-y-4'>
              <div className='flex w-full flex-row items-center justify-between'>
                <Heading level={3}>
                  {isLoading ? (
                    <Skeleton width={200} />
                  ) : (
                    t('discover.talent_match_section.chance_and_risk')
                  )}
                </Heading>
                <AssistantButton
                  onClick={onClickAssistantChanceAndRisk}
                  isLoading={isLoading}
                />
              </div>
              {isSkillsAvailable && (
                <SkillsInsights
                  title={t('global.skills')}
                  group_match={skills.group_match}
                  category_fit={skills.category_fit}
                  evaluation_level={skills.evaluation_level}
                  isLoading={isLoading}
                  onClick={onClickSkillsInsights}
                />
              )}
              {isCultureAvailable && (
                <CultureInsights
                  title={t('global.culture')}
                  group_match={culture.group_match}
                  category_fit={culture.category_fit}
                  evaluation_level={culture.evaluation_level}
                  isLoading={isLoading}
                  onClick={onClickCultureInsights}
                />
              )}
              {isPersonalityAvailable && (
                <PersonalityInsights
                  title={t('global.personality')}
                  group_match={personality.group_match}
                  category_fit={personality.category_fit}
                  evaluation_level={personality.evaluation_level}
                  isLoading={isLoading}
                  onClick={onClickPersonalityInsights}
                />
              )}
              {isRiaSecAvailable && (
                <ProfessionalInterestsInsights
                  title={t('global.professionalInterests')}
                  group_match={interests.group_match}
                  category_fit={interests.category_fit}
                  evaluation_level={interests.evaluation_level}
                  isLoading={isLoading}
                  onClick={onClickProfessionalInterestsInsights}
                />
              )}
            </div>
            <div id='matching-profile-recommendations' className='space-y-4'>
              <div className='flex w-full flex-row items-center justify-between'>
                <Heading level={3}>
                  {isLoading ? (
                    <Skeleton width={200} />
                  ) : (
                    t('discover.talent_match_section.recommandations')
                  )}
                </Heading>
                <AssistantButton
                  onClick={onClickAssistantRecommendations}
                  isLoading={isLoading}
                />
              </div>

              <InterviewRecommendation
                interviews={interviews}
                interviews_label={interviews_label}
                isLoading={isLoading}
              />
              <HiringInitiatives
                measures={measures}
                measures_label={measures_label}
                isLoading={isLoading}
              />
            </div>
          </div>
          <RelatedCareersSection
            className={'mt-8'}
            talentId={talent?.owner}
            spaceId={talent?.id}
            isLoading={isLoading}
          />
          <div className='print-report-section mt-6'>
            <PrintButton
              onClick={onClickDownloadReport}
              text={t('discover.talent_match_section.print_button')}
              isLoading={isLoading}
            />
          </div>
        </div>
      )}
      <CompletionModal
        open={shouldShowCompletionOnboardingModal}
        setOpen={setShouldShowCompletionOnboardingModal}
      />
      <CompletionSummaryModal
        onCompletionClose={() => setShowFeedbackModal(true)}
      />
      <HowToContinueModal
        open={showHowToContinueModal}
        setOpen={setShowHowToContinueModal}
      />
      <DashboardUnlockedJoyride
        run={runDashboardUnlockedJoyride}
        onClose={() => {
          setRunDashboardUnlockedJoyride(false)
          delayedAction(() => {
            enableAssistant()
            sendMessageToAssistant({
              id: 'your-turn',
              role: MESSAGE_ROLES.ASSISTANT,
              type: MESSAGE_TYPES.VIDEO,
              videoMessageProps: {
                title: t('discover.assistant_onboarding.14.title'),
                description: t('discover.assistant_onboarding.14.description'),
                isPulsing: true,
                videoSrc:
                  language === 'de'
                    ? 'https://aivy-assets.s3.eu-central-1.amazonaws.com/videos/discover-5-de.mp4'
                    : 'https://aivy-assets.s3.eu-central-1.amazonaws.com/videos/discover-5-en.mp4',
                ctaPrimary: {
                  title: t('discover.assistant_onboarding.14.cta'),
                  onClick: () => {
                    trackEvent('discover_completed')
                    hideAssistant()
                    setShowHowToContinueModal(true)
                  }
                }
              }
            })
          }, 500)
        }}
      />
      <AssistantSpotlight
        selector={assistantSpotlightSelector}
        scrollOffset={60}
      />
    </div>
  )
}

TalentMatchSection.propTypes = {
  className: PropTypes.string,
  career: PropTypes.object,
  talent: PropTypes.object,
  matchingProfile: PropTypes.object,
  isLoading: PropTypes.bool,
  isDatasetComplete: PropTypes.bool
}
